/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IAccountPreference,
    IAccountPreferences,
    IAccountProfileItem,
    IAccountProfileViewProps
} from '@msdyn365-commerce-modules/account-management/src/modules/account-profile/./account-profile';
import { IAccountProfileProps } from '../definition-extensions/account-profile.ext.props.autogenerated';
import moment from 'moment';

const AccountPreferencesSection: React.FC<IAccountPreferences> = ({ accountPreferences, heading, personalization, extraPreferences }) => {
    return (
        <Node {...accountPreferences}>
            {heading}
            <AccountPreferenceSection {...personalization} />
            {extraPreferences &&
                extraPreferences.map((preferenceSecton, index) => {
                    return <AccountPreferenceSection {...preferenceSecton} key={index} />;
                })}
        </Node>
    );
};

const AccountPreferenceSection: React.FC<IAccountPreference> = ({
    accountPreference,
    heading,
    description,
    buttonWrapper,
    buttonYesText,
    button,
    buttonNoText
}) => {
    return (
        <Node {...accountPreference}>
            {heading}
            {description}
            <Node {...buttonWrapper}>
                {buttonNoText}
                {button}
                {buttonYesText}
            </Node>
        </Node>
    );
};

const AccountProfileSection: React.FC<IAccountProfileItem> = ({ AccountProfileItem, heading, links, description }) => {
    return (
        <Node {...AccountProfileItem}>
            {heading}
            {description}
            {links}
        </Node>
    );
};

const renderAccessExpiryDate = (props: IAccountProfileViewProps & IAccountProfileProps<{}>) => {
    const { accessLabel, indefiniteText } = props.resources;
    const accessEndDate = props.data.customerInformation.result?.ExtensionProperties?.find(x => x.Key?.toLowerCase() === 'enddate')?.Value
        ?.StringValue;
    const approverStatus = props.data.customerInformation.result?.ExtensionProperties?.find(x => x.Key?.toLowerCase() === 'approverstatus')
        ?.Value?.BooleanValue;
    const formattedEndDate = accessEndDate && moment(accessEndDate, 'MM/DD/YYYY').format('MMMM DD, YYYY');
    // If approverstatu is true and "accessEndDate" is blank then access will be indefinite
    if (approverStatus && !accessEndDate) {
        return (
            <div className='account-profile__access-expiry-date'>
                <strong className='ms-account-profile__section-heading'>{accessLabel}</strong> {indefiniteText}
            </div>
        );
    }
    return (
        <div className='account-profile__access-expiry-date'>
            <strong className='ms-account-profile__section-heading'>{accessLabel}</strong> {accessEndDate && formattedEndDate}
        </div>
    );
};

const AccountProfileView: React.FC<IAccountProfileViewProps & IAccountProfileProps<{}>> = props => {
    const {
        AccountProfile,
        accountProfileWrapper,
        heading,
        emailSection,
        nameSection,
        preferenceSection,
        customerAttributesWrapper,
        customerAttributesSection
    } = props;

    return (
        <Module {...AccountProfile}>
            {accountProfileWrapper && (
                <Node {...accountProfileWrapper}>
                    {heading}
                    <AccountProfileSection {...emailSection} />
                    <AccountProfileSection {...nameSection} />
                    {renderAccessExpiryDate(props)}
                    <AccountPreferencesSection {...preferenceSection} />
                </Node>
            )}
            {customerAttributesWrapper && <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>}
        </Module>
    );
};

export default AccountProfileView;
